import {
  Icon,
  ProductAccordionTypes,
  Skeleton,
  SkeletonTypes,
  Text,
  UI,
  HStack,
  VStack,
} from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Pressable } from 'native-base';
import React, { Fragment, useEffect, useState } from 'react';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { SignedInNavigationScreenNavigationProp } from '../../../../routes/types';
import { useManagementAnalysisActions } from '../../../../store/managementAnalysis/actions';
import { selectedInvestmentResumeYearState } from '../../../../store/managementAnalysis/atom';
import { investmentResumeDataQuery } from '../../../../store/managementAnalysis/selector';
import { NormalizedDistributionProductInformation } from '../../../../store/managementAnalysis/types';
import { CheckingAccountCard } from '../CheckingAccountCard';
import { EmptyProducts } from '../EmptyProducts';
import { ProductDistribution, ProductDistributionTypes } from '../ProductDistribution';

const testId = 'investment-resume';

export declare namespace InvestmentResumeTypes {
  interface Props
    extends Omit<
      ProductDistributionTypes.CommonProps,
      'loading' | 'activeDataKey' | 'setActiveDataKey' | 'products' | 'mainInfo' | 'secondaryInfo'
    > {
    yearsLoading: boolean;
    yearsError?: boolean;
    years?: string[];
    alertText: string;
  }
}

const ColoredSkeleton = (props: SkeletonTypes.Props) => (
  <Skeleton {...props} endColor={Color['brand.primary.lightest']} />
);

export const InvestmentResume = ({
  isMobile,
  alertText,
  years,
  yearsLoading,
  yearsError,
}: InvestmentResumeTypes.Props) => {
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { setSelectedInvestmentResumeYear } = useManagementAnalysisActions();
  const { data: yearSelected } = useRecoilQuery(selectedInvestmentResumeYearState);
  const {
    loading: dataLoading,
    error: dataError,
    data,
  } = useRecoilQuery(investmentResumeDataQuery);
  const loading = yearsLoading || dataLoading;
  const hasError = yearsError || dataError;

  const mainInfo = {
    label: 'Meus investimentos',
    value: Utils.Mask.ScaleMoney(data?.information.totalInvested),
  };

  const secondaryInfo = [
    { label: 'Produto principal', value: data?.information.mainInvestedClassNormalized },
    { label: 'Categoria principal', value: data?.information.mainInvestedSubClassNormalized },
  ];

  const updatedInText = `Dados referentes: ${Utils.Mask.DateFormat(
    data?.information.executionDate,
    'dd/MM/yyyy'
  )}`;

  const getDefaultAccordionState = (): Record<string, ProductAccordionTypes.Status> | undefined => {
    return data?.productDistribution.reduce(
      (acc, { productClassName }) => ({ ...acc, [productClassName]: 'closed' }),
      {}
    );
  };

  const [productsSorted, setProductsSorted] = useState(data?.productDistribution);
  const [activeDataKey, setActiveDataKey] = useState<string>();
  const [accordionStates, setAccordionStates] = useState<
    Record<string, ProductAccordionTypes.Status> | undefined
  >(getDefaultAccordionState());

  useEffect(() => {
    if (activeDataKey) {
      if (productsSorted?.length) {
        setProductsSorted([
          ...productsSorted.sort((a) => (a.productClassName === activeDataKey ? -1 : 1)),
        ]);
      }

      setAccordionStates({ ...getDefaultAccordionState(), [activeDataKey]: 'opened' });
    } else {
      setProductsSorted([...(data?.productDistribution || [])]);
      setAccordionStates({ ...getDefaultAccordionState() });
    }
  }, [activeDataKey]);

  useEffect(() => {
    if (!data?.productDistribution?.length) {
      setProductsSorted([]);
      return;
    }

    setProductsSorted([...data.productDistribution]);
    setAccordionStates(getDefaultAccordionState());
  }, [data?.productDistribution]);

  const renderProducts = () => {
    if (hasError) {
      return (
        <VStack alignItems="center" paddingY={Spacing['stack.xxxl']} height="100%">
          <VStack alignItems="center" space={Spacing['stack.nano']}>
            <Icon
              name="Warning"
              testId="error-icon"
              color={Color['feedback.attention.default']}
              size="md"
            />
            <Text fontSize={Typography['fontsize.lg']} variant="semibold" color="neutral.black">
              Ops, Algo deu errado!!
            </Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.black">
              No momento não conseguimos te mostrar os resultados.
            </Text>
            <Text fontSize={Typography['fontsize.md']} color="neutral.black">
              Tente novamente em alguns instantes.
            </Text>
          </VStack>
        </VStack>
      );
    }

    if (loading) {
      return (
        <>
          {Array.from({ length: 5 }).map((_, index) => (
            <UI.ProductCard
              key={index.toString()}
              testId={`productAccordionLoading-${index}`}
              loading={true}
              color="brand.primary.lightest"
              product={{ name: '', percentage: 0, value: 0 }}
            />
          ))}
        </>
      );
    }

    if (!productsSorted?.length) {
      return <EmptyProducts />;
    }

    if (isMobile) {
      return (
        <>
          {productsSorted.map((product, index) => {
            return (
              <Pressable
                key={index.toString()}
                onPress={() =>
                  navigate('ManagementAnalysisDetails', {
                    distributionProductInformation:
                      data?.information as NormalizedDistributionProductInformation,
                    product,
                    alertText,
                  })
                }>
                <UI.ProductCard
                  key={product.productClassName}
                  loading={loading}
                  testId={product.productClassName}
                  color={product.color}
                  size={60}
                  valueLabelTitle="VALOR"
                  product={{
                    name: product.label,
                    percentage: product.productClassPercentage || 0,
                    value: product.productClassValue || 0,
                  }}
                />
              </Pressable>
            );
          })}
        </>
      );
    }

    return (
      <>
        {productsSorted.map((product) => {
          return (
            <UI.ProductAccordion
              key={product.productClassName}
              loading={loading}
              testId={product.productClassName}
              color={product.color}
              chartSize={60}
              product={{
                name: product.label,
                percentage: product.productClassPercentage || 0,
                percentageFormatted: product.productClassPercentageFormatted,
                value: product.productClassValue || 0,
                items: product.productSubClassData?.map((subItem) => ({
                  name: subItem.productSubClass || '',
                  percentage: subItem.productSubClassPercentage,
                  percentageFormatted: subItem.productSubClassPercentageFormatted || '',
                  value: subItem.productSubClassValue,
                })),
              }}
              status={accordionStates?.[product.productClassName]}
              onIconPress={(newState) =>
                setAccordionStates({
                  ...accordionStates,
                  [product.productClassName]: newState,
                })
              }
              handleSeeMorePress={() =>
                navigate('ManagementAnalysisDetails', {
                  distributionProductInformation:
                    data?.information as NormalizedDistributionProductInformation,
                  product,
                  alertText,
                })
              }
            />
          );
        })}
      </>
    );
  };

  const yearOptions = years?.map((year) => ({ label: year, value: year })) || [];

  return (
    <UI.PeriodSelectorPanel
      testId={testId}
      isMobile={isMobile}
      onPeriodChange={setSelectedInvestmentResumeYear}
      title="DISTRIBUIÇÃO POR PRODUTO"
      options={yearOptions}
      selectedOption={yearSelected || ''}
      w={'100%'}
      boxPadding="inset.sm"
      fullWidth
      updatedInText={updatedInText}
      updatedInTextMargin={Spacing['inline.xxs']}>
      <HStack testID={testId} w={'full'}>
        <VStack space={Spacing['stack.xxs']} w={'full'}>
          {!hasError && (
            <Fragment>
              <ProductDistribution
                loading={loading}
                mainInfo={mainInfo}
                products={data?.productDistribution}
                isMobile={isMobile}
                alertText={alertText}
                secondaryInfo={secondaryInfo}
                activeDataKey={activeDataKey}
                setActiveDataKey={setActiveDataKey}
              />
              <CheckingAccountCard investmentResumeLoading={loading} isMobile={isMobile} />
            </Fragment>
          )}
          <VStack space={Spacing['stack.nano']}>
            {loading && !hasError && (
              <ColoredSkeleton
                width={126}
                height={'14px'}
                rounded={'full'}
                marginBottom={Spacing['stack.xxxs']}
              />
            )}
            {!loading && !hasError && (
              <UI.Text
                mb={Spacing['stack.xxxs']}
                fontSize={Typography['fontsize.lg']}
                variant="semibold">
                ALOCAÇÃO POR PRODUTO
              </UI.Text>
            )}
            {renderProducts()}
          </VStack>
        </VStack>
      </HStack>
    </UI.PeriodSelectorPanel>
  );
};
